import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, TrendingUp, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

/**
 * InvestorHeatmapCard — Command Centre demand-signal panel.
 *
 *   • 28-day sparkline of Express Interest submissions
 *   • Ticket-size tag-cloud weighted by submission count
 *   • Top-firm callout + 28-day total + unread badge
 *
 * Backend: GET /api/vault/prospect-alerts/heatmap (vault auth)
 * Polls every 60s. Gracefully hides if the vault session isn't open yet —
 * the founder will see it the moment they unlock the Vault.
 */

const PALETTE = {
  bg: '#FFFFFF',
  border: 'rgba(92,82,106,0.10)',
  coral: '#F99B7D',
  mint: '#A3E2C9',
  lavender: '#9BB8ED',
  text: '#2A2235',
  muted: '#5C526A',
  cream: '#FAFAED',
};

const TAG_HUES = [PALETTE.coral, PALETTE.lavender, PALETTE.mint, '#E9C46A', '#B392AC', '#7FB7BE'];

const InvestorHeatmapCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    try {
      // Vault-authed endpoint — check the session first (session-check never
      // 401s) so a locked vault doesn't spam the console with 401 errors.
      const sess = await apiClient.request('/api/vault/session-check').catch(() => null);
      if (!sess?.active) {
        setHidden(true);
        return;
      }
      const res = await apiClient.request('/api/vault/prospect-alerts/heatmap');
      if (res?.daily_counts) {
        setData(res);
        setHidden(false);
      } else {
        setHidden(true);
      }
    } catch {
      setHidden(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const i = setInterval(load, 60000); return () => clearInterval(i); }, [load]);

  const { sparklinePath, peakIdx, peakCount, maxCount } = useMemo(() => {
    if (!data?.daily_counts?.length) return { sparklinePath: '', peakIdx: -1, peakCount: 0, maxCount: 0 };
    const counts = data.daily_counts.map(d => d.count);
    const max = Math.max(1, ...counts);
    const w = 100, h = 28;
    const step = w / (counts.length - 1);
    const pts = counts.map((c, i) => {
      const x = i * step;
      const y = h - (c / max) * h;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    });
    let peakIdx = 0;
    counts.forEach((c, i) => { if (c > counts[peakIdx]) peakIdx = i; });
    return { sparklinePath: pts.join(' '), peakIdx, peakCount: counts[peakIdx], maxCount: max };
  }, [data]);

  if (hidden) return null;
  if (loading) {
    return (
      <div className="rounded-2xl p-5 flex items-center gap-3" style={{ background: PALETTE.bg, border: `1px solid ${PALETTE.border}` }} data-testid="investor-heatmap-loading">
        <Loader2 size={16} className="animate-spin" style={{ color: PALETTE.coral }} />
        <span className="text-sm" style={{ color: PALETTE.muted }}>Loading investor demand signals…</span>
      </div>
    );
  }
  if (!data) return null;

  const distribution = data.ticket_distribution || [];
  const totals = data.totals || {};
  const total28d = totals.total_28d || 0;
  const maxDistCount = Math.max(1, ...distribution.map(d => d.count));

  // Tag-cloud weight: minimum 12px, scaling up to 22px based on share.
  const tagFontSize = (count) => 12 + Math.round((count / maxDistCount) * 10);

  return (
    <div
      className="rounded-2xl p-6"
      style={{ background: PALETTE.bg, border: `1px solid ${PALETTE.border}`, borderTop: `3px solid ${PALETTE.coral}` }}
      data-testid="investor-heatmap-card"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: PALETTE.coral + '22' }}>
            <Activity size={16} style={{ color: '#9A4A33' }} />
          </div>
          <div>
            <h3 className="text-sm font-bold leading-tight" style={{ color: PALETTE.text }}>Investor Demand Heatmap</h3>
            <p className="text-xs" style={{ color: PALETTE.muted }}>Last 28 days · live from /investors gateway</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black leading-none" style={{ color: PALETTE.text }} data-testid="heatmap-total-28d">{total28d}</div>
          <div className="text-xs uppercase tracking-wider" style={{ color: PALETTE.muted }}>submissions</div>
        </div>
      </div>

      {/* Sparkline */}
      <div className="mb-5" data-testid="heatmap-sparkline">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: PALETTE.muted }}>
            Daily submissions
          </span>
          {peakCount > 0 && (
            <span className="text-xs inline-flex items-center gap-1" style={{ color: PALETTE.text }}>
              <TrendingUp size={11} style={{ color: PALETTE.coral }} />
              Peak <strong>{peakCount}</strong> on <strong>{data.daily_counts[peakIdx]?.date}</strong>
            </span>
          )}
        </div>
        <div className="relative" style={{ background: PALETTE.cream, borderRadius: 12, padding: '12px 14px' }}>
          <svg viewBox="0 0 100 28" preserveAspectRatio="none" style={{ width: '100%', height: 64, display: 'block' }} aria-hidden="true">
            <defs>
              <linearGradient id="sparkfill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={PALETTE.coral} stopOpacity="0.32" />
                <stop offset="100%" stopColor={PALETTE.coral} stopOpacity="0" />
              </linearGradient>
            </defs>
            {sparklinePath && (
              <>
                <polyline points={`${sparklinePath} 100,28 0,28`} fill="url(#sparkfill)" />
                <polyline points={sparklinePath} fill="none" stroke={PALETTE.coral} strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round" />
                {peakCount > 0 && data.daily_counts[peakIdx] && (() => {
                  const step = 100 / (data.daily_counts.length - 1);
                  const x = peakIdx * step;
                  const y = 28 - (peakCount / maxCount) * 28;
                  return <circle cx={x} cy={y} r="1.2" fill={PALETTE.coral} stroke="#fff" strokeWidth="0.4" />;
                })()}
              </>
            )}
          </svg>
          <div className="flex justify-between mt-2 text-xs" style={{ color: PALETTE.muted }}>
            <span>{totals.window_start}</span>
            <span>{totals.window_end}</span>
          </div>
        </div>
      </div>

      {/* Ticket-size tag cloud */}
      <div data-testid="heatmap-ticket-cloud">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: PALETTE.muted }}>
            Ticket-size cluster
          </span>
          {totals.top_firm && (
            <span className="text-xs" style={{ color: PALETTE.muted }}>
              Most-active firm: <strong style={{ color: PALETTE.text }}>{totals.top_firm}</strong>
            </span>
          )}
        </div>
        {distribution.length === 0 ? (
          <p className="text-xs px-3 py-2 rounded-lg" style={{ background: PALETTE.cream, color: PALETTE.muted }} data-testid="heatmap-empty">
            No submissions in the last 28 days. The gate is quiet — drive a tweet or share /investors directly.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {distribution.map((d, i) => (
              <span
                key={d.bucket}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-semibold"
                style={{
                  background: TAG_HUES[i % TAG_HUES.length] + '22',
                  color: PALETTE.text,
                  border: `1px solid ${TAG_HUES[i % TAG_HUES.length]}55`,
                  fontSize: `${tagFontSize(d.count)}px`,
                  lineHeight: 1.2,
                }}
                data-testid={`heatmap-tag-${d.bucket.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                title={`${d.count} submission${d.count === 1 ? '' : 's'} · ${d.pct}%`}
              >
                {d.bucket}
                <span className="text-xs font-black opacity-70">{d.count}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvestorHeatmapCard;
